// 1. React
import React, { useCallback, useEffect, useState } from "react";

// 2. Third-party
import { Card, Spin, Result, Alert, Form, Button, message } from "antd";
import moment from "moment";
import queryString from "query-string";

// 3. Components
import SchemaFormRenderer, { assertPublicUploadSize } from "@iso/components/SchemaFormRenderer";
import VisitorDetailsFields from "@iso/containers/Pages/Visitor/VisitorDetailsFields";

// 4. Services
import { visitorInductionService } from "@iso/services";
import { VISITOR_INFO_TYPES } from "@iso/constants/visitor";

// Where the visitor is in the flow. One value rather than several booleans, so
// the page can never show two phases at once.
const PHASE = {
  LOADING: "loading",
  ERROR: "error",
  // Confirm-your-details before the questions, mirroring the staff flow and the
  // check-in landing's `checkin_info` step. Saves to the visitor record.
  DETAILS: "details",
  FORM: "form",
  ALREADY_DONE: "already_done",
  DONE: "done",
};

/**
 * The induction a visitor completes on their own phone, from the link staff
 * sent them.
 *
 * Unauthenticated. The `token` in the URL identifies ONE induction for ONE
 * visitor — not a form — so nothing here can be replayed against another
 * record, and induction forms never have to be made publicly resolvable the way
 * check-in forms are.
 */
const PublicInduction = () => {
  const [phase, setPhase] = useState(PHASE.LOADING);
  const [errorMessage, setErrorMessage] = useState("");
  const [form, setForm] = useState(null);
  const [context, setContext] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [detailsForm] = Form.useForm();
  const [savingDetails, setSavingDetails] = useState(false);
  const detailsType = Form.useWatch("visitor_type", detailsForm) || VISITOR_INFO_TYPES.VISITOR;

  const token = queryString.parse(window.location.search).token || "";

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!token) {
        setErrorMessage("This induction link is missing its code.");
        setPhase(PHASE.ERROR);
        return;
      }
      try {
        const res = await visitorInductionService.getPublic(token);
        if (cancelled) return;
        if (!res || !res.success) {
          setErrorMessage((res && res.message) || "This induction link is not valid.");
          setPhase(PHASE.ERROR);
          return;
        }
        setForm(res.data.form);
        setContext({
          visitorName: res.data.visitor_name,
          propertyName: res.data.property_name,
          // Suggestions for any "Property Area / Location" field. Names only —
          // this link is opened by an anonymous visitor.
          areas: res.data.areas || [],
        });
        const v = res.data.visitor || {};
        detailsForm.setFieldsValue({
          visitor_type: v.visitor_type || VISITOR_INFO_TYPES.VISITOR,
          name: v.name,
          email: v.email,
          phone: v.phone,
          business_name: v.business_name,
          birthday: v.birthday ? moment(v.birthday) : undefined,
          emergency_name: v.emergency_name,
          emergency_contact: v.emergency_contact,
          emergency_mobile_number: v.emergency_mobile_number,
          scheduled_arrival:
            v.scheduled_arrival || v.checkin_time
              ? moment(v.scheduled_arrival || v.checkin_time)
              : moment(),
        });
        // A worker-owned induction (run from a contractor's Personnel register)
        // has no visit behind it and so no visitor record to confirm — skip
        // straight to the questions.
        const hasVisitor = !!res.data.visitor;
        // ALREADY submitted — test completed_at, not state === "completed".
        // Since verification landed, a submitted induction sits at `pending`
        // until someone accepts it, so a state check would hand the visitor
        // the whole form again on a link they had already finished, and the
        // submit would then fail with "already been completed" — an error
        // screen for someone who had done nothing wrong.
        setPhase(
          res.data.induction.completed_at
            ? PHASE.ALREADY_DONE
            : hasVisitor
            ? PHASE.DETAILS
            : PHASE.FORM
        );
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to open induction:", e);
        setErrorMessage("We could not open this induction. Please try the link again.");
        setPhase(PHASE.ERROR);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [token, detailsForm]);

  // File questions cannot use the check-in upload endpoint — that one only
  // accepts a primary check-in form's token. Uploads here are gated by the
  // induction token instead.
  const uploadFile = useCallback(
    async (file) => {
      assertPublicUploadSize(file);
      const fd = new FormData();
      fd.append("File", file);
      fd.append("token", token);
      fd.append("type", "induction_upload");
      const res = await visitorInductionService.uploadPublicFile(fd);
      return res && res.success && res.data && res.data.file ? res.data.file : null;
    },
    [token]
  );

  const handleSubmit = async (answers) => {
    setSubmitting(true);
    try {
      const res = await visitorInductionService.submitPublic(token, answers);
      if (res && res.success) {
        setPhase(PHASE.DONE);
      } else {
        setErrorMessage((res && res.message) || "We could not record your induction.");
        setPhase(PHASE.ERROR);
      }
    } catch (e) {
      console.error("Failed to submit induction:", e);
      setErrorMessage("We could not record your induction. Please try again.");
      setPhase(PHASE.ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  // Save the details, then move on to the questions. A failure keeps the
  // visitor on this step with the reason, rather than dropping them into a form
  // whose details never saved.
  const saveDetails = async () => {
    let values;
    try {
      values = await detailsForm.validateFields();
    } catch (e) {
      return;
    }
    setSavingDetails(true);
    try {
      // visitor_type and scheduled_arrival are read-only here and the endpoint
      // refuses them anyway — see VisitorInductionService::updatePublicDetails.
      const res = await visitorInductionService.submitPublicDetails(token, {
        name: values.name,
        email: values.email,
        phone: values.phone,
        business_name: values.business_name,
        birthday: values.birthday ? values.birthday.format("YYYY-MM-DD") : "",
        emergency_name: values.emergency_name,
        emergency_contact: values.emergency_contact,
        emergency_mobile_number: values.emergency_mobile_number,
      });
      if (res && res.success) {
        setPhase(PHASE.FORM);
      } else {
        message.error((res && res.message) || "We could not save your details.");
      }
    } catch (e) {
      message.error("We could not save your details. Please try again.");
    } finally {
      setSavingDetails(false);
    }
  };

  const renderBody = () => {
    switch (phase) {
      case PHASE.LOADING:
        return (
          <div style={{ display: "flex", justifyContent: "center", padding: "64px 0" }}>
            <Spin size="large" />
          </div>
        );
      case PHASE.ERROR:
        return (
          <Result
            status="warning"
            title="This induction can't be opened"
            subTitle={errorMessage}
          />
        );
      case PHASE.ALREADY_DONE:
        return (
          <Result
            status="success"
            title="You've already completed this induction"
            subTitle="There's nothing more to do — thanks."
          />
        );
      case PHASE.DETAILS:
        return (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="First, confirm your details"
              description={
                context.propertyName
                  ? `${context.propertyName} keeps these on file for your visit. Please correct anything that's wrong and add an emergency contact.`
                  : "Please correct anything that's wrong and add an emergency contact."
              }
            />
            <Form form={detailsForm} layout="vertical" name="publicInductionDetails">
              <VisitorDetailsFields
                form={detailsForm}
                visitorType={detailsType}
                typeReadOnly
                arrivalReadOnly
              />
            </Form>
            <div style={{ textAlign: "right", marginTop: 8 }}>
              <Button type="primary" size="large" loading={savingDetails} onClick={saveDetails}>
                Save &amp; continue
              </Button>
            </div>
          </>
        );
      case PHASE.DONE:
        return (
          <Result
            status="success"
            title="Induction submitted"
            subTitle={`Thanks${context.visitorName ? `, ${context.visitorName}` : ""}. Your answers have been recorded. ${
              context.propertyName || "The property"
            } will review them before your visit — there is nothing more for you to do.`}
          />
        );
      default:
        return (
          <>
            {/* One warning line, no form name. The visitor is already on the
                form — naming it again tells them nothing; what they need is why
                they are here and before when. */}
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                context.propertyName
                  ? `Please complete this before visiting ${context.propertyName}.`
                  : "Please complete this before your visit."
              }
            />
            <SchemaFormRenderer
              form={form}
              areas={context.areas}
              loading={submitting}
              onSubmit={handleSubmit}
              uploadFile={uploadFile}
            />
          </>
        );
    }
  };

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "24px 16px" }}>
      <Card
        title={
          context.visitorName
            ? `Induction for ${context.visitorName}`
            : "Site induction"
        }
      >
        {renderBody()}
      </Card>
    </div>
  );
};

export default PublicInduction;
